import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { FixedSizeList as List } from "react-window";
import { UserContext } from "../../../context";
import GenerateReports from "./components/generate-reports";
import styles from "./dashboard.module.css";
import {
  TDropdownOptions,
  TProject,
  TProjectCategory,
} from "../../../types/config";
import { Select, Empty, Tooltip, Input, Modal, Button } from "antd";
import KanbanSkeleton from "../../../components/loading/kanban-skeleton";
import {
  CloseOutlined,
  LeftCircleOutlined,
  RightCircleOutlined,
  CheckCircleFilled,
  ReloadOutlined,
  FolderAddOutlined,
  FileAddOutlined,
  UploadOutlined,
  BarChartOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import TaskCard from "../../../components/task-card/task-card";
import {
  AddEditTask,
  getAssessmentByUserId,
  GetProjectCategoryList,
  GetProjectList,
  GetSpecificProjectDashboardData,
  GetTasksList,
} from "../../../utils/api.constant";
import useFetch from "../../../hooks/useFetch";
import { eASSESSMENT_STATUS, ePrivileges, eResultCode } from "../../../utils/enum";
import { PageSize } from "../../../utils/constants";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import useToast from "../../../hooks/useToast";
import CustomDrawer from "../../../components/drawer";
import TaskDetailsModal from "../../../components/task-card/task-details";
import Utils from "../../../utils";

import {
  TTaskWithWorkInfo,
  TStatusColumn,
} from "../../../types/dashboard.types";
import UploadExcel from "./components/excel-upload/upload-excel";
import TasksDrawer from "./components/tasks-list-drawer/tasks-drawer";
import AddEditWorkForm from "./components/add-edit-work/work-form";
import AddEditTaskForm from "./components/add-edit-task/task-form";
import PerformanceForm from "../assessment/kpis/performance";
import ViewPerformanceForm from "../assessment/performance/viewperformance";

function ImprovedDashboard() {
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const context: any = useContext(UserContext);
  console.log("context", context);

  const activeCycle = context?.activeCycle;

  // State management
  const [statusColumns, setStatusColumns] = useState<TStatusColumn[]>([]);
  const [projectOptions, setProjectOptions] = useState<TDropdownOptions[]>([]);
  const [selectedProject, setSelectedProject] = useState<string | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(false);
  const [projectCategoryOptions, setProjectCategoryOptions] = useState<
    TDropdownOptions[]
  >([]);
  const [activeProjectCategory, setActiveProjectCategory] =
    useState<TDropdownOptions>();
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTaskForUpdate, setSelectedTaskForUpdate] = useState<any>(null);
  const [targetStatusId, setTargetStatusId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [assessmentStatus, setAssessmentStatus] = useState<eASSESSMENT_STATUS | null>(null);
  const [assessmentId, setAssessmentId] = useState<number | null>(null);
  const [returnCode, setReturnCode] = useState<number | null>(null);

  // Ref to track the last fetched project to prevent infinite loops
  const lastFetchedProjectRef = useRef<string | null>(null);

  // Ref for the kanban wrapper to enable auto-scroll during drag
  const kanbanWrapperRef = useRef<HTMLDivElement>(null);

  // Use ref instead of state for draggedTask to prevent re-renders during drag
  const draggedTaskRef = useRef<TTaskWithWorkInfo | null>(null);

  // Auto-scroll state for drag operations
  const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Burgundy color shades from CSS variables
  const colorSets = [
    "var(--primary-shade-1)",
    "var(--primary-shade-2)",
    "var(--primary-shade-3)",
    "var(--primary-shade-4)",
  ];

  // Fetch all tasks for the selected project and organize by status
  const fetchAllTasks = useCallback(
    async (projectId: string, forceRefresh: boolean = false) => {
      if (!projectId) return;

      // Prevent infinite loops by checking if we've already fetched this project
      if (!forceRefresh && lastFetchedProjectRef.current === projectId) {
        return;
      }

      setIsLoading(true);
      try {
        // First get the project dashboard data to get work categories and statuses
        const dashboardResponse = await post(GetSpecificProjectDashboardData, {
          data: { id: projectId },
        });

        if (dashboardResponse.dataResponse.returnCode === eResultCode.SUCCESS) {
          const dashboardData = dashboardResponse.data;
          const statusMap = new Map<number, TStatusColumn>();

          // Initialize status columns
          dashboardData.project.projectStatusList.forEach((category: any) => {
            statusMap.set(category.statusId, {
              statusId: category.statusId,
              statusName: category.statusName,
              tasks: [],
              totalCount: category.taskCount || 0,
            });
          });

          // Create a map of work items for quick lookup
          // const workItemsMap = new Map<number, any>();
          // dashboardData.projectStatusList.forEach((category: any) => {
          //   category.workList.forEach((workItem: any) => {
          //     workItemsMap.set(workItem.projectWorkMappingId, {
          //       ...workItem,
          //       statusId: category.statusId,
          //     });
          //   });
          // });

          // Fetch all tasks for the project in a single API call
          const tasksResponse = await post(GetTasksList, {
            data: {
              projectId: projectId,
            },
          });

          if (tasksResponse.dataResponse.returnCode === eResultCode.SUCCESS) {
            const allTasks = tasksResponse.data;

            // Organize tasks by status
            allTasks.forEach((task: any) => {
              // const workItem = workItemsMap.get(task.projectWorkMappingId);

              const formattedTask: TTaskWithWorkInfo = {
                taskCategoryId: task.id,
                taskCategoryName: task.name,
                taskDescription: task.description,
                assignedToUserId: task.assignToUserId,
                employeeName: task.employeeName || "Unassigned",
                priority: task.priority,
                startDate: task.startDateTime,
                endDate: task.endDateTime,
                timeLeft: task.timeLeft || "2",
                estimatedTime: task?.estimatedTime || "",
                actualTime: task?.actualTime || "",
                managerName: task?.managerName || "",
                ticketNumber: task?.ticketNumber || `T-${task.id}`,
                statusId: task.statusId,
                projectWorkMappingId: task.projectWorkMappingId,
                // Work info
                workCategoryName: task?.workCategoryName || "Unknown",
                workTitle: task?.workTitle || "",
                workCategoryId: task?.workCategoryId || 0,
                // Mock additional data - replace with real API data
                commentsCount: task?.commentsCount || 0,
                // subtasksCount: Math.floor(Math.random() * 3),
                attachmentsCount: task?.attachmentsCount || 0,
                assignedUser: task?.assignedUser || [],
              };

              const statusColumn = statusMap.get(task.statusId);
              if (statusColumn) {
                statusColumn.tasks.push(formattedTask);
              }
            });
          }

          setStatusColumns(Array.from(statusMap.values()));
          // Update the ref to track the last fetched project
          lastFetchedProjectRef.current = projectId;
        }
        // Fetch assessment status
        // const assessmentResponse = await post(getAssessmentByUserId, {
        //   data: {
        //     userId: context?.user?.id,
        //   },
        // });
        // const responseCode = assessmentResponse.dataResponse.returnCode;
        // setReturnCode(responseCode);

        // // if (assessmentResponse.dataResponse.returnCode === eResultCode.SUCCESS) {
        // //   const assessment = assessmentResponse.data;
        // //   console.log("statusName:", assessment?.statusName, typeof assessment?.statusName);
        // //   setAssessmentStatus(assessment?.statusName || null);
        // //   setAssessmentId(assessment?.id || null);
        // // }
        // if (responseCode === eResultCode.SUCCESS) {
        //   const assessment = assessmentResponse.data;

        //   setAssessmentStatus(assessment?.statusName || null);
        //   setAssessmentId(assessment?.id ?? null);
        // } else if (responseCode === 2) {
        //   // important: state reset
        //   setAssessmentId(null);
        //   setAssessmentStatus(null);
        // }
      } catch (error) {
        console.error("Error fetching tasks:", error);
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: "Failed to fetch tasks",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [post, onShowToast]
  );
  const fetchAssessment = async () => {
    try {
      const assessmentResponse = await post(getAssessmentByUserId, {
        data: {
          userId: context?.user?.id,
        },
      });

      const responseCode = assessmentResponse?.dataResponse?.returnCode;
      setReturnCode(responseCode);

        console.log("responseCode-----", responseCode);
      if (responseCode === eResultCode.SUCCESS) {
        const assessment = assessmentResponse?.data;


        if (assessment?.id) {
          // ✅ Assessment exists
          setAssessmentId(assessment.id);
          setAssessmentStatus(assessment.statusName ?? null);
        } else {
          // ✅ Success but no data (null case)
          setAssessmentId(null);   // 👈 important change
          setAssessmentStatus(null);
        }
      } else {
        // ❌ API failure
        setAssessmentId(null);
        setAssessmentStatus(null);
      }
    } catch (error) {
      console.error("Assessment API error:", error);

      setAssessmentId(null);
      setAssessmentStatus(null);
    }
  };

  useEffect(() => {
    if (context?.user?.id) {
      fetchAssessment();
    }
  }, [context?.user?.id]);
  // Drag and drop handlers - Memoized for performance
  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, task: TTaskWithWorkInfo) => {
      // Use ref instead of state to prevent re-render on first drag
      draggedTaskRef.current = task;
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", task.taskCategoryId.toString());

      // Create a custom drag image for smoother dragging
      const dragElement = e.currentTarget;
      if (dragElement instanceof HTMLElement) {
        const clone = dragElement.cloneNode(true) as HTMLElement;
        clone.style.position = "absolute";
        clone.style.top = "-9999px";
        clone.style.opacity = "0.7";
        clone.style.transform = "rotate(2deg)";
        clone.style.pointerEvents = "none";
        clone.style.width = "280px"; // Fixed smaller width for drag image
        clone.style.maxWidth = "280px";
        document.body.appendChild(clone);
        e.dataTransfer.setDragImage(clone, 140, 100); // Center the drag image

        // Clean up clone after drag image is created
        requestAnimationFrame(() => {
          document.body.removeChild(clone);
        });

        // Visual feedback - use class instead of direct style manipulation
        dragElement.classList.add("dragging");
      }
    },
    []
  );

  const handleDragEnd = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLElement) {
      e.target.classList.remove("dragging");
    }
    draggedTaskRef.current = null;

    // Clear auto-scroll interval
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";

      // Auto-scroll logic embedded in dragover for better reliability
      if (!kanbanWrapperRef.current || !draggedTaskRef.current) return;

      const container = kanbanWrapperRef.current;
      const scrollThreshold = 150; // Increased threshold for easier triggering
      const scrollSpeed = 20; // Increased scroll speed

      const containerRect = container.getBoundingClientRect();
      const mouseX = e.clientX;

      // Only proceed if we have valid mouse coordinates
      if (mouseX === 0) return;

      // Calculate distance from edges
      const distanceFromLeft = mouseX - containerRect.left;
      const distanceFromRight = containerRect.right - mouseX;

      // Clear existing interval
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }

      // Scroll left when near left edge
      if (distanceFromLeft < scrollThreshold && distanceFromLeft > 0) {
        autoScrollIntervalRef.current = setInterval(() => {
          if (container.scrollLeft > 0) {
            container.scrollLeft -= scrollSpeed;
          } else {
            // Stop scrolling when reached the beginning
            if (autoScrollIntervalRef.current) {
              clearInterval(autoScrollIntervalRef.current);
              autoScrollIntervalRef.current = null;
            }
          }
        }, 16); // ~60fps
      }
      // Scroll right when near right edge
      else if (distanceFromRight < scrollThreshold && distanceFromRight > 0) {
        autoScrollIntervalRef.current = setInterval(() => {
          const maxScroll = container.scrollWidth - container.clientWidth;
          if (container.scrollLeft < maxScroll) {
            container.scrollLeft += scrollSpeed;
          } else {
            // Stop scrolling when reached the end
            if (autoScrollIntervalRef.current) {
              clearInterval(autoScrollIntervalRef.current);
              autoScrollIntervalRef.current = null;
            }
          }
        }, 16); // ~60fps
      }
    },
    [] // No dependencies needed since we use refs
  );

  // Simplified handleDrag - no longer needed for auto-scroll but kept for compatibility
  const handleDrag = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // Empty function - auto-scroll now handled in handleDragOver
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>, targetStatusId: number) => {
      e.preventDefault();

      const draggedTask = draggedTaskRef.current;
      if (!draggedTask || draggedTask.statusId === targetStatusId) {
        return;
      }

      // Open modal for additional details if needed
      setSelectedTaskForUpdate(draggedTask);
      setTargetStatusId(targetStatusId);
      setIsTaskModalOpen(true);
    },
    []
  );

  const handleTaskStatusUpdate = async (updatedTaskData: any) => {
    if (!selectedTaskForUpdate || !targetStatusId) return;

    try {
      const response = await post(AddEditTask, {
        data: {
          ...updatedTaskData,
          statusId: targetStatusId,
        },
      });

      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        // Update local state
        setStatusColumns((prevColumns) => {
          return prevColumns.map((column) => {
            if (column.statusId === selectedTaskForUpdate.statusId) {
              // Remove from source column
              return {
                ...column,
                tasks: column.tasks.filter(
                  (task) =>
                    task.taskCategoryId !== selectedTaskForUpdate.taskCategoryId
                ),
                totalCount: Math.max(0, column.totalCount - 1),
              };
            } else if (column.statusId === targetStatusId) {
              // Add to target column
              // Preserve assignedUser array from selectedTaskForUpdate to prevent data loss
              const updatedTask = {
                ...selectedTaskForUpdate,
                statusId: targetStatusId,
                ...updatedTaskData,
                // Make sure to preserve assignedUser with full user details
                assignedUser: selectedTaskForUpdate.assignedUser || [],
              };
              return {
                ...column,
                tasks: [...column.tasks, updatedTask],
                totalCount: column.totalCount + 1,
              };
            }
            return column;
          });
        });

        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.centerTop,
          content: "Task status updated successfully",
        });
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Failed to update task status",
      });
    } finally {
      setIsTaskModalOpen(false);
      setSelectedTaskForUpdate(null);
      setTargetStatusId(null);
    }
  };

  const handleStatusChange = async (taskId: number, newStatusId: number) => {
    const task = statusColumns
      .flatMap((column) => column.tasks)
      .find((t) => t.taskCategoryId === taskId);

    if (task) {
      setSelectedTaskForUpdate(task);
      setTargetStatusId(newStatusId);
      setIsTaskModalOpen(true);
    }
  };

  const handleAssigneeChange = async (taskId: number, assigneeId: number) => {
    // Implement assignee change logic
    console.log("Assignee change:", taskId, assigneeId);
  };

  // Fetch project categories on mount
  const fetchProjectCategories = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await post(GetProjectCategoryList, {
        data: { currentPage: 1, pageSize: PageSize },
      });

      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        const formattedData = response.data.map((item: TProjectCategory) => ({
          label: item.name,
          value: item.id,
        }));
        setProjectCategoryOptions(formattedData);
        setActiveProjectCategory(formattedData[0]);

        setIsDataLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching project categories:", error);
    } finally {
      setIsLoading(false);
    }
  }, [post]);

  const fetchProjectOptions = useCallback(
    async (projectCategory: TDropdownOptions) => {
      setIsLoading(true);
      try {
        const response = await post(GetProjectList, {
          data: {
            pageSize: PageSize,
            projectCategoryId: projectCategory.value,
            currentPage: 1,
          },
        });

        if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
          if (response.data?.length) {
            const options = response.data.map((project: TProject) => ({
              value: project.id,
              label: project.name,
            }));
            setProjectOptions(options);
            // Reset the ref when changing projects
            lastFetchedProjectRef.current = null;
            setSelectedProject(options[0].value);
          } else {
            setProjectOptions([]);
            setSelectedProject(undefined);
            setStatusColumns([]);
            lastFetchedProjectRef.current = null;
          }
        }
      } catch (error) {
        console.error("Error fetching project options:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [post]
  );

  // Effects
  useEffect(() => {
    fetchProjectCategories();
  }, [fetchProjectCategories]);

  useEffect(() => {
    if (activeProjectCategory) {
      fetchProjectOptions(activeProjectCategory);
    }
  }, [activeProjectCategory, fetchProjectOptions]);

  useEffect(() => {
    if (selectedProject) {
      // Reset the ref when project changes
      lastFetchedProjectRef.current = null;
      fetchAllTasks(selectedProject);
    }
  }, [selectedProject]); // Removed fetchAllTasks dependency to prevent infinite loop

  // Cleanup effect
  useEffect(() => {
    return () => {
      lastFetchedProjectRef.current = null;
    };
  }, []);

  const handleProjectChange = (value: string) => {
    setSelectedProject(value);
  };

  const handleRefresh = () => {
    if (selectedProject) {
      // Reset the ref to allow refetching
      lastFetchedProjectRef.current = null;
      fetchAllTasks(selectedProject, true); // Force refresh
    }
  };

  // Filter tasks based on search query
  const filterTasksBySearch = (
    tasks: TTaskWithWorkInfo[]
  ): TTaskWithWorkInfo[] => {
    if (!searchQuery.trim()) return tasks;

    const query = searchQuery.toLowerCase();
    return tasks.filter(
      (task) =>
        task.taskCategoryName.toLowerCase().includes(query) ||
        task.taskDescription.toLowerCase().includes(query) ||
        task.employeeName.toLowerCase().includes(query) ||
        task?.workCategoryName?.toLowerCase().includes(query) ||
        task?.workTitle?.toLowerCase().includes(query) ||
        (task.ticketNumber && task.ticketNumber.toLowerCase().includes(query))
    );
  };

  // Get filtered status columns based on search
  const filteredStatusColumns = statusColumns.map((column) => ({
    ...column,
    tasks: filterTasksBySearch(column.tasks),
  }));

  // Calculate search results
  const totalTasks = statusColumns.reduce(
    (sum, column) => sum + column.tasks.length,
    0
  );
  const filteredTasks = filteredStatusColumns.reduce(
    (sum, column) => sum + column.tasks.length,
    0
  );
  const hasSearchQuery = searchQuery.trim().length > 0;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  // Memoized callbacks for TaskCard props - prevents recreation on every render
  const handleRefreshTaskList = useCallback(() => {
    if (selectedProject) {
      lastFetchedProjectRef.current = null;
      fetchAllTasks(selectedProject, true);
    }
  }, [selectedProject, fetchAllTasks]);

  const handleTaskUpdate = useCallback(() => {
    if (selectedProject) {
      console.log("this is called , ", selectedProject);
      lastFetchedProjectRef.current = null;
      fetchAllTasks(selectedProject, true);
    }
  }, [selectedProject, fetchAllTasks]);

  const handleSetNewTaskDetails = useCallback(() => { }, []);

  // Memoize status options to prevent recreation
  const statusOptionsForTasks = useCallback(
    () =>
      statusColumns.map((col) => ({
        label: col.statusName,
        value: col.statusId,
      })),
    [statusColumns]
  );

  return (
    <>
      {/* Project Category Tabs */}
      <div className={styles.scrollContainer}>
        <button
          className={styles.scrollButton}
          onClick={() => {
            const container = document.getElementById("scrollableContainer");
            if (container)
              container.scrollBy({ left: -200, behavior: "smooth" });
          }}
        >
          <LeftCircleOutlined className={styles.icon} />
        </button>
        <div
          id='scrollableContainer'
          className={styles.parentContainer}
        >
          {projectCategoryOptions?.map(
            (projectCategory: TDropdownOptions, index: number) => (
              <div
                key={`${projectCategory.label + index}`}
                className={`${styles.tab} ${activeProjectCategory?.label === projectCategory.label
                  ? styles.activeTab
                  : ""
                  }`}
                onClick={() => setActiveProjectCategory(projectCategory)}
              >
                <span
                  className={
                    activeProjectCategory?.label === projectCategory.label
                      ? styles.activeText
                      : styles.inactiveText
                  }
                >
                  {projectCategory.label}
                </span>
              </div>
            )
          )}
        </div>
        <button
          className={styles.scrollButton}
          onClick={() => {
            const container = document.getElementById("scrollableContainer");
            if (container)
              container.scrollBy({ left: 200, behavior: "smooth" });
          }}
        >
          <RightCircleOutlined className={styles.icon} />
        </button>
      </div>

      {/* Main Dashboard */}
      <div className={styles.dashboardContainer}>
        {/* Header Controls */}
        <div className={styles.headerControls}>
          <div className={styles.projectSelector}>
            <label
              htmlFor='project-select'
              className={styles.projectLabel}
            >
              Project -
            </label>
            <Select
              id='project-select'
              placeholder='Choose a project'
              className={styles.projectSelect}
              value={selectedProject}
              onChange={handleProjectChange}
              options={projectOptions}
            />
            <Tooltip title='Refresh'>
              <ReloadOutlined
                className={styles.refreshIcon}
                onClick={handleRefresh}
                style={{
                  opacity: isLoading ? 0.5 : 1,
                  cursor: isLoading ? "not-allowed" : "pointer",
                }}
              />
            </Tooltip>
          </div>

          {/* Global Search */}

          <div className={styles.actionButtons}>
            {/* <CustomDrawer
              width={1980}
              trigger={
                <Button type="primary" className={styles.flashBtn} icon={<ThunderboltOutlined />}>
                  Start your assessment
                </Button>
              }
              component={PerformanceForm}
              componentProps={{
                id: 0, // Pass necessary props for ServicePartnerForm
                onRefreshList: () => {
                  // will update the context later
                },
              }}
              title="Submit Performance"
            /> */}

            <>
  {/* ✅ START BUTTON → ONLY ACTIVE CYCLE */}
  {activeCycle && assessmentId === null && (
    <CustomDrawer
      width={1980}
      trigger={
        <Button
          type="primary"
          className={styles.flashBtn}
          icon={<ThunderboltOutlined />}
        >
          Start your assessment
        </Button>
      }
      component={PerformanceForm}
      componentProps={{
        id: 0,
        onRefreshList: () => {},
      }}
      title="Submit Performance"
    />
  )}

  {/* ✅ VIEW BUTTON → ALWAYS (WITH STATUS CHECK) */}
  {assessmentId !== null &&
    (
      assessmentStatus === eASSESSMENT_STATUS.MANAGER_REVIEW ||
      assessmentStatus === eASSESSMENT_STATUS.EMPLOYEE_VERIFY ||
      assessmentStatus === eASSESSMENT_STATUS.HR_REVIEW ||
      assessmentStatus === eASSESSMENT_STATUS.FINAL_ACCEPTANCE
    ) && (
      <CustomDrawer
        width={1980}
        trigger={<Button>View Performance</Button>}
        component={ViewPerformanceForm}
        componentProps={{
          id: assessmentId,
          onRefreshList: () => {},
        }}
        title="View Performance"
      />
    )}
</>
            {/* {assessmentId === null ? (
              <CustomDrawer
                width={1980}
                trigger={
                  <Button
                    type="primary"
                    className={styles.flashBtn}
                    icon={<ThunderboltOutlined />}
                  >
                    Start your assessment
                  </Button>
                }
                component={PerformanceForm}
                componentProps={{
                  id: 0,
                  onRefreshList: () => { },
                }}
                title="Submit Performance"
              />
            ) : (assessmentStatus === null || assessmentStatus === eASSESSMENT_STATUS.EMPLOYEE_VERIFY ||
              assessmentStatus === eASSESSMENT_STATUS.HR_REVIEW ||
              assessmentStatus === eASSESSMENT_STATUS.FINAL_ACCEPTANCE) ? (
              <CustomDrawer
                width={1980}
                trigger={<Button>View Performance</Button>}
                component={ViewPerformanceForm}
                componentProps={{
                  id: assessmentId,
                  onRefreshList: () => { },
                }}
                title="View Performance"
              />
            ) : null} */}
            <div className={styles.searchContainer}>
              <Input
                placeholder='Search tasks...'
                prefix={
                  <SearchOutlined
                    className={styles.searchIcon}
                    style={{ color: "var( --bs-search-style)" }}
                  />
                }
                value={searchQuery}
                onChange={handleSearchChange}
                allowClear
                onClear={clearSearch}
                className={styles.searchInput}
              />
            </div>
            {Utils.isUserHasAccess(
              context?.user?.privilegeList,
              ePrivileges.ADDWORK
            ) && (
                <CustomDrawer
                  size='large'
                  trigger={
                    <Tooltip title='Upload Excel'>
                      <UploadOutlined className={styles.headerActionIcon} />
                    </Tooltip>
                  }
                  component={UploadExcel}
                  componentProps={{
                    projectId: selectedProject,
                    projectName:
                      projectOptions.find((p) => p.value === selectedProject)
                        ?.label || "Unknown Project",
                    projectCategoryId: activeProjectCategory?.value,
                    onRefreshList: () => {
                      if (selectedProject) {
                        lastFetchedProjectRef.current = null;
                        fetchAllTasks(selectedProject, true);
                      }
                    },
                  }}
                  title='Upload Excel'
                />
              )}
            {Utils.isUserHasAccess(
              context?.user?.privilegeList,
              ePrivileges.GENERATEREPORT
            ) && (
                <CustomDrawer
                  size='default'
                  trigger={
                    <Tooltip title='Generate Reports'>
                      <BarChartOutlined className={styles.headerActionIcon} />
                    </Tooltip>
                  }
                  component={GenerateReports}
                  title='Generate Reports'
                />
              )}
          </div>
        </div>

        {/* Kanban Board */}
        {isLoading ? (
          <KanbanSkeleton
            columns={4}
            cardsPerColumn={3}
          />
        ) : (
          <div
            className={styles.kanbanWrapper}
            ref={kanbanWrapperRef}
          >
            {filteredStatusColumns.length > 0 ? (
              filteredStatusColumns.map(
                (column: TStatusColumn, index: number) => (
                  <div
                    key={column.statusId}
                    className={styles.kanbanColumn}
                    style={{
                      borderTop: `4px solid ${colorSets[index % colorSets.length]}`,
                    }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, column.statusId)}
                  >
                    {/* Column Header */}
                    <div className={styles.columnHeader}>
                      <div className={styles.columnTitle}>
                        <h3
                          style={{ color: colorSets[index % colorSets.length] }}
                        >
                          {column.statusName}
                        </h3>
                        <span
                          className={styles.taskCount}
                          style={{
                            backgroundColor:
                              colorSets[index % colorSets.length],
                          }}
                        >
                          {column.tasks.length}
                        </span>
                      </div>

                      <div className={styles.columnActions}>
                        <CustomDrawer
                          width={1400}
                          trigger={
                            <Tooltip title='List View'>
                              <UnorderedListOutlined
                                className={styles.actionIcon}
                                style={{
                                  color: colorSets[index % colorSets.length],
                                }}
                              />
                            </Tooltip>
                          }
                          component={TasksDrawer}
                          componentProps={{
                            projectId: selectedProject,
                            statusId: column.statusId,
                            projectName:
                              projectOptions.find(
                                (p) => p.value === selectedProject
                              )?.label || "Unknown Project",
                            statusName: column.statusName,
                            projectCategory: activeProjectCategory,
                            tasks: column.tasks,
                          }}
                          title={`${column.statusName} Tasks - ${projectOptions.find((p) => p.value === selectedProject)?.label || "Project"}`}
                        />

                        {Utils.isUserHasAccess(
                          context?.user?.privilegeList,
                          ePrivileges.ADDWORK
                        ) && (
                            <CustomDrawer
                              size='large'
                              trigger={
                                <Tooltip title='Add Work'>
                                  <FolderAddOutlined
                                    className={styles.actionIcon}
                                    style={{
                                      color: colorSets[index % colorSets.length],
                                    }}
                                  />
                                </Tooltip>
                              }
                              component={AddEditWorkForm}
                              componentProps={{
                                id: 0,
                                projectId: selectedProject,
                                projectCategoryId: activeProjectCategory?.value,
                                onRefreshList: () => {
                                  if (selectedProject) {
                                    lastFetchedProjectRef.current = null;
                                    fetchAllTasks(selectedProject, true);
                                  }
                                },
                              }}
                              title='Add Work'
                            />
                          )}

                        {Utils.isUserHasAccess(
                          context?.user?.privilegeList,
                          ePrivileges.ADDTASK
                        ) && (
                            <CustomDrawer
                              size='large'
                              trigger={
                                <Tooltip title='Add Task'>
                                  <FileAddOutlined
                                    className={styles.actionIcon}
                                    style={{
                                      color: colorSets[index % colorSets.length],
                                    }}
                                  />
                                </Tooltip>
                              }
                              component={AddEditTaskForm}
                              componentProps={{
                                id: 0,
                                statusId: column.statusId,
                                projectId: selectedProject,
                                projectName:
                                  projectOptions.find(
                                    (p) => p.value === selectedProject
                                  )?.label || "Unknown Project",
                                onRefreshList: () => {
                                  if (selectedProject) {
                                    lastFetchedProjectRef.current = null;
                                    fetchAllTasks(selectedProject, true);
                                  }
                                },
                              }}
                              title='Add Task'
                            />
                          )}
                      </div>
                    </div>

                    {/* Column Body */}
                    <div className={styles.columnBody}>
                      {column.tasks.length > 0 ? (
                        <List
                          height={window.innerHeight - 250}
                          itemCount={column.tasks.length}
                          itemSize={220}
                          width='100%'
                          overscanCount={2}
                        >
                          {({ index: taskIndex, style }) => {
                            const task = column.tasks[taskIndex];
                            return (
                              <div
                                style={{
                                  ...style,
                                  padding: "12px",
                                }}
                              >
                                <TaskCard
                                  key={task.taskCategoryId}
                                  task={task}
                                  onDragStart={(e) => handleDragStart(e, task)}
                                  onDragEnd={handleDragEnd}
                                  onDrag={handleDrag}
                                  statusOptions={statusOptionsForTasks()}
                                  statusId={column.statusId}
                                  projectId={Number(selectedProject)}
                                  projectCategoryId={activeProjectCategory?.value?.toString()}
                                  color={colorSets[index % colorSets.length]}
                                  fetchSpecificProjectDashboardData={
                                    handleRefreshTaskList
                                  }
                                  setNewTaskDetails={handleSetNewTaskDetails}
                                  updateTask={handleTaskUpdate}
                                  workId={Number(task.projectWorkMappingId)}
                                  workCategoryName={task.workCategoryName}
                                  workTitle={task.workTitle}
                                  onStatusChange={handleStatusChange}
                                  onAssigneeChange={handleAssigneeChange}
                                />
                              </div>
                            );
                          }}
                        </List>
                      ) : (
                        <Empty
                          description='No tasks in this status'
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          className={styles.emptyState}
                        />
                      )}
                    </div>
                  </div>
                )
              )
            ) : hasSearchQuery && filteredTasks === 0 ? (
              <div className={styles.emptySearchResults}>
                <Empty
                  description={`No tasks found matching "${searchQuery}"`}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </div>
            ) : (
              <div className={styles.noDataContainer}>
                <Empty
                  description='No projects available for this category'
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </div>
            )}
          </div>
        )}

        {/* Search Results Indicator */}
        {hasSearchQuery && (
          <div className={styles.searchResultsIndicator}>
            {filteredTasks === 0
              ? `No tasks found for "${searchQuery}"`
              : filteredTasks === totalTasks
                ? `Showing all ${totalTasks} tasks`
                : `Showing ${filteredTasks} of ${totalTasks} tasks matching "${searchQuery}"`}
          </div>
        )}
      </div>

      {/* Task Update Modal */}
      <Modal
        title={
          selectedTaskForUpdate
            ? `${selectedTaskForUpdate.ticketNumber || `T-${selectedTaskForUpdate.taskCategoryId}`} - ${selectedTaskForUpdate.taskCategoryName}`
            : "Update Task"
        }
        open={isTaskModalOpen}
        onOk={() => { }}
        onCancel={() => setIsTaskModalOpen(false)}
        footer={null}
        width='70%'
      >
        {selectedTaskForUpdate ? (
          <TaskDetailsModal
            id={selectedTaskForUpdate.taskCategoryId}
            statusOptions={statusColumns.map((col) => ({
              label: col.statusName,
              value: col.statusId,
            }))}
            setIsModalOpen={setIsTaskModalOpen}
            handleTaskUpdate={handleTaskStatusUpdate}
            selectedProject={selectedProject}
            setTargetStatusId={setTargetStatusId}
            currentDropData={{
              targetCategory: {
                statusId: targetStatusId,
                statusName:
                  statusColumns.find((col) => col.statusId === targetStatusId)
                    ?.statusName || "Unknown",
              },
            }}
            fetchSpecificProjectDashboardData={() => {
              if (selectedProject) {
                lastFetchedProjectRef.current = null;
                fetchAllTasks(selectedProject, true);
              }
            }}
          />
        ) : (
          <p>No task selected.</p>
        )}
      </Modal>
    </>
  );
}

export default ImprovedDashboard;
